import React, { useEffect, useRef, useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import EmblaCarousel from 'embla-carousel';

// import NavigationAccessories from '../components/Navigation/Accessories';
import NavigationItem from '../components/Navigation/NavigationItem';
import CarouselNavButton from '../components/Navigation/CarouselNavButton';
import {
    // editProduct,
    // removeProduct,
    // selectSection,
    selectSectionAndCategory,
    setActiveFilterButton,
    setSkinTone,
    // toggleNavigation,
    SELECTING_SECTION,
    RECEIVED_PRODUCTS,
} from '../actions';
import SelectedProduct from '../entities/SelectedProduct';

/**
 * Navigation container
 *
 * @author John Noel <john.noel@joipolloi.com>
 * @author Matt Haynes <matt.haynes@joiopolloi.com>
 * @package Minifigs
 */

const Navigation = ({
    activeSection = null,
    dispatch,
    setEnableBuilder = null,
    skintone = null,
    loading = false,
    sections: navigationItems = [],
    currentBuildIndex,
}) => {
    const sliderRef = useRef(null);
    const prevBtnRef = useRef(null);
    const nextBtnRef = useRef(null);
    const emblaApiRef = useRef(null);
    // Prevent the initial Embla 'select' event from overwriting an existing
    // navigation.selected (was causing unwanted default to 'skinTone').
    const hasInitialisedRef = useRef(false);
    const [canScrollPrev, setCanScrollPrev] = useState(false);
    const [canScrollNext, setCanScrollNext] = useState(false);
    const skintoneRef = useRef(skintone);
    const prevActiveSectionRef = useRef(activeSection);
    const prevSectionsRef = useRef(navigationItems);
    const navItemsRef = useRef([]);
    // Lock navigation interactions while a section/category fetch is in-flight to prevent spammy clicks
    const [navLocked, setNavLocked] = useState(false);
    const lastNavIntentRef = useRef(null);

    useEffect(() => {
        skintoneRef.current = skintone;
    }, [skintone]);

    // Augment navigation with a leading Skin Tone item
    const navItems = useMemo(() => {
        const skinToneItem = {
            id: 'skinTone',
            title: 'Skin tone',
            emptyText: 'Choose a skin tone',
            handle: null,
        };
        return [skinToneItem, ...(navigationItems || [])];
    }, [navigationItems]);

    // Keep ref of latest nav items for Embla event callbacks
    useEffect(() => {
        navItemsRef.current = navItems;
    }, [navItems]);

    const handleNavItemSelect = useCallback(
        (catInfo) => {
            if (!catInfo) return;
            if (navLocked && catInfo.id !== 'skinTone') return;
            if (loading && catInfo.id !== 'skinTone') return;

            // Skin tone: special case (never triggers a product fetch / loading state)
            if (catInfo.id === 'skinTone') {
                if (activeSection !== 'skinTone') {
                    dispatch({ type: SELECTING_SECTION, payload: 'skinTone' });
                    dispatch({ type: RECEIVED_PRODUCTS, payload: [] });
                }
                if (
                    skintone &&
                    emblaApiRef.current &&
                    (lastNavIntentRef.current === 'click-skinTone' || lastNavIntentRef.current === 'embla-select-init')
                ) {
                    emblaApiRef.current.scrollTo(1);
                }
                return;
            }

            if (!skintone) {
                dispatch(setSkinTone(currentBuildIndex, 'Yellow'));
            }

            setNavLocked(true);

            dispatch(setActiveFilterButton(null));
            dispatch(selectSectionAndCategory(catInfo, null, skintone || 'Yellow'));
        },
        [activeSection, currentBuildIndex, dispatch, skintone, loading, navLocked]
    );

    useEffect(() => {
        if (!loading) setNavLocked(false);
    }, [loading]);

    useEffect(() => {
        if (!sliderRef.current) return;

        const initialIndex = (navItems || []).findIndex((item) => item.id === activeSection);
        emblaApiRef.current = EmblaCarousel(sliderRef.current, {
            align: 'start',
            loop: false,
            dragFree: false,
            containScroll: 'trimSnaps',
        });
        if (initialIndex > -1) emblaApiRef.current.scrollTo(initialIndex, true);

        const embla = emblaApiRef.current;

        const updateScrollButtons = () => {
            if (!embla) return;
            setCanScrollPrev(embla.canScrollPrev());
            setCanScrollNext(embla.canScrollNext());
        };

        const onSelect = () => {
            if (!embla) return;
            if (!hasInitialisedRef.current) return;
            lastNavIntentRef.current = 'embla-select';
            const index = embla.selectedScrollSnap();
            const items = navItemsRef.current || [];
            const selectedItem = items[index];
            if (!selectedItem) return;
            if (selectedItem.id !== activeSection) {
                handleNavItemSelect(selectedItem);
            }
            updateScrollButtons();
        };
        embla.on('select', onSelect);
        embla.on('reInit', updateScrollButtons);
        updateScrollButtons();

        setTimeout(() => {
            hasInitialisedRef.current = true;
        }, 0);

        (async () => {
            if (!skintoneRef.current) dispatch(setSkinTone(currentBuildIndex, 'Yellow'));
            setEnableBuilder?.(true);
        })();

        return () => {
            embla.off('select', onSelect);
            embla.off('reInit', updateScrollButtons);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [handleNavItemSelect]);

    useEffect(() => {
        const prevActiveSection = prevActiveSectionRef.current;
        const prevSections = prevSectionsRef.current;
        if (emblaApiRef.current && (prevActiveSection !== activeSection || prevSections !== navigationItems)) {
            const index = (navItems || []).findIndex((item) => item.id === activeSection);
            if (index > -1) emblaApiRef.current.scrollTo(index, true);
            emblaApiRef.current.reInit();
        }
        prevActiveSectionRef.current = activeSection;
        prevSectionsRef.current = navigationItems;
    }, [activeSection, navigationItems, navItems]);

    return (
        <nav className="navigation">
            <div className="navigation__sectionNavigation">
                <h2 className="navigation__subTitle">Start Building</h2>

                <CarouselNavButton
                    direction="prev"
                    ref={prevBtnRef}
                    disabled={!canScrollPrev || navLocked || loading}
                    onClick={() => {
                        if (!emblaApiRef.current) return;
                        lastNavIntentRef.current = 'prev';
                        emblaApiRef.current.scrollPrev();
                    }}
                />
                <CarouselNavButton
                    direction="next"
                    ref={nextBtnRef}
                    disabled={!canScrollNext || navLocked || loading}
                    onClick={() => {
                        if (!emblaApiRef.current) return;
                        lastNavIntentRef.current = 'next';
                        if (!skintone) {
                            emblaApiRef.current.scrollTo(0, true);
                            return;
                        }
                        emblaApiRef.current.scrollNext();
                    }}
                />

                <div
                    className="navigation__sectionNavigationButtons embla"
                    ref={sliderRef}
                    style={{ overflow: 'hidden' }}
                >
                    <div className="embla__container" style={{ display: 'flex' }}>
                        {navItems &&
                            navItems.map((navItem) => (
                                <div className="embla__slide swiper-slide" key={navItem.id}>
                                    <NavigationItem
                                        {...navItem}
                                        key={navItem.id}
                                        isActive={activeSection == navItem.id}
                                        categoryInfo={navItem}
                                        displayProducts={(catInfo) => {
                                            lastNavIntentRef.current = `click-${catInfo?.id}`;
                                            handleNavItemSelect(catInfo);
                                        }}
                                        loading={loading || navLocked}
                                    />
                                </div>
                            ))}
                    </div>
                </div>
            </div>
        </nav>
    );
};

Navigation.propTypes = {
    activeSection: PropTypes.string,
    dispatch: PropTypes.func.isRequired,
    setEnableBuilder: PropTypes.func,
    skintone: PropTypes.string,
    loading: PropTypes.bool,
    sections: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.string.isRequired,
            title: PropTypes.string,
            handle: PropTypes.string,
            emptyText: PropTypes.string,
        })
    ),
    currentBuildIndex: PropTypes.number.isRequired,
};

const mapStateToProps = (state) => {
    return {
        currentBuildIndex: state.currentBuildIndex,
        selectedCategory: state.browser.selectedCategory,
        cost: SelectedProduct.calculateCost(state.builds[state.currentBuildIndex].selected),
        activeSection: state.navigation.selected,
        skintone: state.builds[state.currentBuildIndex].skintone,
        torsoOverlays: state.builds[state.currentBuildIndex].torsoOverlays,
        loading: state.loading,
        sections: state.sections,
        buying: state.buying,
        showNavigation: state.showNavigation,
    };
};

export default connect(mapStateToProps)(Navigation);
